import { useCallback, useEffect, useState } from 'react';
import { X, Gavel, History } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchLotsANegocier, fixerPrixLot } from '../../lib/dataProvider';
import { getCoursDuJour } from '../../lib/cours';
import { useAuth } from '../../lib/auth';
import { fmt } from '../shared/badges';
import { couleurGain, couleurCout, NEUTRE_DOUX } from '../../lib/couleurs';
import { AideNegociation } from './AideNegociation';

const pct = (v) => Number(v ?? 0).toFixed(2).replace('.', ',');

const th = 'px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500';
const td = 'px-3 py-2 align-top';
const field =
  'mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';

// Fixation du prix définitif des achats « À négocier » (Administrateur & Pont-Bascule) :
// l'historique des cours figés sert de base de négociation.
export const FixationPrixDialog = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const cours = getCoursDuJour();
  const [lots, setLots] = useState([]);
  const [choix, setChoix] = useState(null);
  const [histo, setHisto] = useState(false);

  const load = useCallback(() => { fetchLotsANegocier().then(setLots); }, []);
  useEffect(() => { load(); }, [load]);

  const valider = async () => {
    const { error, montantFinal, provision, ecart, ecartSurVentes, ajustements } = await fixerPrixLot({
      id: choix.id,
      methode: choix.methode,
      prixBaseHuile: choix.prixBaseHuile,
      prixFixe: choix.prixFixe,
      note: choix.note,
      utilisateur: user?.name,
    });
    if (error) return toast.error(error);
    toast.success(
      `${choix.id} fixé à ${fmt(montantFinal, 3)} TND — dette fournisseur mise à jour (provision ${fmt(provision, 3)}, écart ${
        ecart >= 0 ? '+' : '−'
      }${fmt(Math.abs(ecart), 3)} TND)${ajustements.length ? ` · CMP réajusté sur ${ajustements.length} cuve(s)` : ''}${
        ecartSurVentes ? ` · ${fmt(Math.abs(ecartSurVentes), 3)} TND en écart de marge sur ventes déjà faites` : ''
      }`,
      { duration: 10000 }
    );
    setChoix(null);
    load();
    onDone?.();
  };

  return (
    <div data-testid="modale-fixation-prix" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4">
      <div className="w-full max-w-6xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black text-slate-900">
              <Gavel size={18} className="text-amber-700" /> Achats à négocier — fixation du prix définitif
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Cours du jour : Extra {fmt(cours.noble, 3)} · Lampante {fmt(cours.lampante, 3)} TND/kg
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="fixation-historique" onClick={() => setHisto((v) => !v)} className="flex h-10 items-center gap-1.5 border border-slate-300 px-3 text-xs font-bold text-slate-600 hover:bg-slate-900 hover:text-white">
              <History size={13} /> Historique des cours
            </button>
            <button type="button" data-testid="fixation-fermer" onClick={onClose} className="border border-slate-300 p-2 text-slate-600 hover:bg-slate-900 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        {histo && (
          <div data-testid="fixation-table-cours" className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <table className="w-full border-collapse">
              <thead>
                <tr>{['Horodatage', 'Auteur', 'Extra / Vierge', 'Lampante'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(cours.historique ?? []).slice(0, 12).map((h, i) => (
                  <tr key={`${h.le}-${i}`}>
                    <td className={`${td} font-data text-xs`}>{String(h.le).slice(0, 16).replace('T', ' ')}</td>
                    <td className={`${td} text-xs`}>{h.par || '—'}</td>
                    <td className={`${td} tabular font-data text-xs`}>{fmt(h.noble, 3)}</td>
                    <td className={`${td} tabular font-data text-xs`}>{fmt(h.lampante, 3)}</td>
                  </tr>
                ))}
                {(cours.historique ?? []).length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-500">Aucun historique : le cours n’a pas encore été modifié.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="table-lots-negocier">
            <thead className="bg-slate-50">
              <tr>{['Bon', 'Date', 'Fournisseur', 'Qualité', 'Olives (Kg)', 'Huile (Kg)', 'Campagne fournisseur', 'Cours figé', 'Cours du jour', 'Écart', 'Provision (TND)', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lots.length === 0 ? (
                <tr><td colSpan={12} data-testid="negocier-vide" className="px-3 py-10 text-center text-sm text-slate-500">Aucun achat en attente de fixation de prix.</td></tr>
              ) : (
                lots.map((l) => (
                  <tr key={l.id} data-testid={`lot-negocier-${l.id}`} className="hover:bg-slate-50">
                    <td className={`${td} font-data text-sm font-bold text-slate-900`}>{l.id}</td>
                    <td className={`${td} font-data text-xs text-slate-600`}>{l.date}</td>
                    <td className={`${td} text-sm text-slate-800`}>{l.fournisseur}</td>
                    <td className={`${td} text-xs text-slate-600`}>{l.qualite}</td>
                    <td className={`${td} tabular text-right font-data text-xs`}>{fmt(l.poidsNet, 1)}</td>
                    <td className={`${td} tabular text-right font-data text-xs`} data-testid={`huile-${l.id}`}>
                      {l.trituree ? (
                        <>
                          {fmt(l.huileProduite, 3)}
                          <span className="block font-bold text-[10px] text-emerald-700">Rdt lot {pct(l.rendement)} %</span>
                        </>
                      ) : (
                        <span className="text-amber-700">— non triturée</span>
                      )}
                    </td>
                    <td className={`${td} font-data text-[11px] text-slate-600`} data-testid={`campagne-${l.id}`}>
                      {l.stats ? (
                        (() => {
                          const q = l.stats.parQualite.find((x) => x.id === l.qualite) ?? l.stats.global;
                          return (
                            <>
                              <span className="tabular font-bold text-slate-900">{fmt(q.olivesKg / 1000, 2)} T</span>
                              <span className="text-slate-400"> · {q.lots} lot(s) {q.label ?? ''}</span>
                              <span className="block">
                                Prix moyen <span className="tabular font-bold text-slate-800">{fmt(q.prixMoyenOlives, 3)}</span> TND/kg olives
                              </span>
                              {q.rendementFiable ? (
                                <span className="block text-slate-500">Rdt moyen {pct(q.rendementMoyen)} %</span>
                              ) : (
                                <span className="block font-bold text-amber-800">Rdt moyen : en attente</span>
                              )}
                            </>
                          );
                        })()
                      ) : '—'}
                    </td>
                    <td className={`${td} tabular text-right font-data text-xs font-bold`} data-testid={`cours-fige-${l.id}`}>
                      {fmt(l.coursFigeValeur, 3)}
                      <span className="block font-normal text-[10px] text-slate-400">
                        {String(l.coursFige?.le ?? '').slice(0, 16).replace('T', ' ')}
                      </span>
                    </td>
                    <td className={`${td} tabular text-right font-data text-xs`}>{fmt(l.coursJour, 3)}</td>
                    <td className={`${td} tabular text-right font-data text-xs ${couleurGain(l.ecartCours, NEUTRE_DOUX)}`}>
                      {l.ecartCours > 0 ? '+' : ''}{fmt(l.ecartCours, 3)}
                    </td>
                    <td className={`${td} tabular text-right font-data text-xs`}>{fmt(l.provisionTnd, 3)}</td>
                    <td className={`${td} text-right`}>
                      <button
                        type="button"
                        data-testid={`fixer-${l.id}`}
                        onClick={() =>
                          setChoix({
                            ...l,
                            methode: 'base',
                            // Base neutre : le prix proposé s'aligne exactement sur la provision
                            // (cours figé) → écart initial de 0 %.
                            prixBaseHuile: String(l.coursFigeValeur || l.coursJour),
                            prixFixe: '',
                            note: '',
                          })
                        }
                        className="border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        Fixer le prix
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {choix && (
          <div data-testid="bloc-fixation" className="border-t-2 border-slate-900 bg-slate-50 px-5 py-4">
            <p className="font-heading text-sm font-bold text-slate-900">
              Prix définitif — {choix.id} · {choix.fournisseur} ({fmt(choix.poidsNet, 1)} kg d’olives, cours figé {fmt(choix.coursFigeValeur, 3)} TND/kg)
            </p>
            <p data-testid="fixation-rendement-lot" className="mt-0.5 font-data text-xs text-slate-700">
              {choix.trituree ? (
                <>
                  Huile produite : <span className="tabular font-black text-slate-900">{fmt(choix.huileProduite, 1)} kg</span> ·{' '}
                  <span className="border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-bold text-emerald-900">
                    Rendement du lot : <span className="tabular">{pct(choix.rendement)} %</span>
                  </span>
                </>
              ) : (
                <span className="border border-amber-400 bg-amber-50 px-1.5 py-0.5 font-bold text-amber-900">
                  Lot non trituré — rendement du lot inconnu
                </span>
              )}
            </p>
            <div className="mt-3">
              <AideNegociation
                stats={choix.stats}
                lot={choix}
                prixPropose={
                  choix.methode === 'fixe'
                    ? Number(choix.prixFixe) || 0
                    : ((choix.rendement || 0) / 100) * (Number(choix.prixBaseHuile) || 0)
                }
              />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Méthode</span>
                <select data-testid="fixation-methode" value={choix.methode} onChange={(e) => setChoix({ ...choix, methode: e.target.value })} className={field}>
                  <option value="base">Prix de base de l’huile (× rendement)</option>
                  <option value="fixe">Prix fixe (TND / kg d’olives)</option>
                </select>
              </label>
              {choix.methode === 'base' ? (
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Prix de base huile (TND/kg)</span>
                  <input data-testid="fixation-prix-base" type="number" step="0.001" min="0" value={choix.prixBaseHuile} onChange={(e) => setChoix({ ...choix, prixBaseHuile: e.target.value })} className={field} />
                </label>
              ) : (
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Prix fixe (TND/kg olives)</span>
                  <input data-testid="fixation-prix-fixe" type="number" step="0.001" min="0" value={choix.prixFixe} onChange={(e) => setChoix({ ...choix, prixFixe: e.target.value })} className={field} />
                </label>
              )}
              <label className="block sm:col-span-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Note de négociation (facultatif)</span>
                <input data-testid="fixation-note" value={choix.note} onChange={(e) => setChoix({ ...choix, note: e.target.value })} placeholder="ex. accord téléphonique du 14/06, cours en baisse" className={field} />
              </label>
            </div>
            <p data-testid="fixation-apercu" className="mt-2 font-data text-xs text-slate-600">
              Provision actuelle : {fmt(choix.provisionTnd, 3)} TND · nouveau montant estimé :{' '}
              {fmt(
                choix.methode === 'fixe'
                  ? (Number(choix.prixFixe) || 0) * choix.poidsNet
                  : ((choix.rendement || 0) / 100) * (Number(choix.prixBaseHuile) || 0) * choix.poidsNet,
                3
              )}{' '}
              TND
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" data-testid="fixation-annuler" onClick={() => setChoix(null)} className="h-11 flex-1 border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
              <button type="button" data-testid="fixation-valider" onClick={valider} className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold text-white hover:bg-slate-700">
                Valider le prix définitif (dette + CMP)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
